/**
 * DRPX Courier — Fahrer Single-Player CRUD (Supabase)
 *
 * Alle CRUD-Operationen für die Fahrer-eigenen Daten: Kunden, eigene
 * Aufträge, Rechnungs-Vorlagen, Buchungen (EÜR), Touren-Protokoll und
 * Auftrags-Vorlagen. Nicht zu verwechseln mit Marktplatz-Aufträgen
 * (siehe `auftraege.ts`).
 *
 * @module lib/supabase/fahrer
 */

import { _sb } from './client.js';
import {
  mapFahrerKundeFromDb,
  mapFahrerAuftragFromDb,
  mapFahrerRechnungFromDb,
  mapFahrerBuchungFromDb,
  mapFahrtFromDb,
  mapFahrerVorlageFromDb,
} from './mappers.js';
import { camelToSnake } from '../mapper.js';
import { extractCity } from '../format.js';
import { grobePosition } from '../geo.js';
import type {
  FahrerKunde,
  FahrerAuftrag,
  FahrerRechnung,
  FahrerBuchung,
  Fahrt,
  FahrerAuftragStatus,
  BuchungTyp,
} from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

// ════════════════════════════════════════════════════════════════════
// Lokale Input-Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

interface FahrerKundeInput extends Partial<FahrerKunde> {}

interface FahrerAuftragCreateInput extends Partial<FahrerAuftrag> {
  /** Foto-Dokumentation (Array an Bild-Metadaten). Nicht in `FahrerAuftrag` enthalten,
   *  weil DB-spezifischer Storage-Pfad. */
  fotoDoku?: unknown[];
}

interface FahrerAuftragUpdateInput extends FahrerAuftragCreateInput {}

interface FahrerRechnungCreateInput extends Partial<FahrerRechnung> {
  // Bank-Snapshot (NICHT in FahrerRechnung, weil nur zur Create-Time aus
  // Profile.bankdaten gelesen und an DB weitergereicht — wird NICHT als
  // eigenes Feld in der Rechnung gespeichert).
  bankIban?: string;
  bankBic?: string;
  bankName?: string;
  steuernr?: string;
}

interface FahrerRechnungUpdateInput extends FahrerRechnungCreateInput {}

interface FahrerBuchungCreateInput extends Partial<FahrerBuchung> {
  quelle?: string;
}

interface FahrerBuchungUpdateInput {
  datum?: string;
  typ?: BuchungTyp;
  kategorie?: string;
  beschreibung?: string;
  betrag?: number;
  kunde?: string;
}

interface FahrtCreateInput extends Partial<Fahrt> {}

interface FahrtUpdateInput {
  datum?: string;
  startOrt?: string;
  zielOrt?: string;
  kmStart?: string;
  kmEnde?: string;
  kmGefahren?: number;
  kunde?: string;
  zweck?: string;
  kennzeichen?: string;
  notizen?: string;
}

interface FahrerVorlageInput {
  kundeName?: string;
  kundeId?: string;
  abhol?: string;
  ziel?: string;
  preis?: string;
  gewicht?: string;
  groesse?: string;
  notizen?: string;
}

// ════════════════════════════════════════════════════════════════════
// Update-Whitelists
// ════════════════════════════════════════════════════════════════════

const KUNDE_UPDATE_WHITELIST = [
  'name', 'ansprech', 'telefon', 'email', 'adresse', 'plz', 'ort', 'notizen',
] as const satisfies readonly (keyof FahrerKundeInput)[];

const AUFTRAG_UPDATE_WHITELIST = [
  'titel', 'kundeId', 'kundeName', 'abhol', 'ziel', 'datum', 'zeit',
  'entladeDatum', 'entladeZeit',
  'gewicht', 'groesse', 'preis', 'notizen', 'status', 'packstuecke', 'fotoDoku',
] as const satisfies readonly (keyof FahrerAuftragUpdateInput)[];

const RECHNUNG_UPDATE_WHITELIST = [
  'status', 'mahnstufe', 'letzteMahnung', 'locked',
  'kundeId', 'kundeName', 'kundeAdresse', 'kundePlz', 'kundeOrt', 'kundeEmail', 'kundeTel',
  'auftragId', 'datum', 'leistungsdatum', 'faellig', 'positionen', 'notizen',
  'zahlungsziel', 'kundenRef', 'kennzeichen', 'route', 'skonto', 'kleinunternehmer',
  'sumNetto', 'sumMwst', 'sumBrutto',
] as const satisfies readonly (keyof FahrerRechnungUpdateInput)[];

const BUCHUNG_UPDATE_WHITELIST = [
  'datum', 'typ', 'kategorie', 'beschreibung', 'betrag', 'kunde',
] as const satisfies readonly (keyof FahrerBuchungUpdateInput)[];

const FAHRT_UPDATE_WHITELIST = [
  'datum', 'startOrt', 'zielOrt', 'kmStart', 'kmEnde', 'kmGefahren',
  'kunde', 'zweck', 'kennzeichen', 'notizen',
] as const satisfies readonly (keyof FahrtUpdateInput)[];

// ════════════════════════════════════════════════════════════════════
// FAHRER KUNDEN
// ════════════════════════════════════════════════════════════════════

/** Lädt alle Kunden eines Fahrers. */
export async function getFahrerKunden(fahrerId: string): Promise<{
  data: FahrerKunde[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_kunden').select('*').eq('fahrer_id', fahrerId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map((row) => mapFahrerKundeFromDb(row)).filter((r): r is FahrerKunde => r !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Legt einen neuen Kunden im Fahrer-Adressbuch an. */
export async function createFahrerKunde(
  fahrerId: string, kunde: FahrerKundeInput,
): Promise<{ data: FahrerKunde | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      fahrer_id: fahrerId,
      name: kunde.name || '', ansprech: kunde.ansprech || '',
      telefon: kunde.telefon || '', email: kunde.email || '',
      adresse: kunde.adresse || '', plz: kunde.plz || '',
      ort: kunde.ort || '', notizen: kunde.notizen || '',
    };
    const r = await client.from('fahrer_kunden').insert(d).select().single();
    return { data: r.data ? mapFahrerKundeFromDb(r.data as Record<string, unknown>) : null, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Aktualisiert einen bestehenden Fahrer-Kunden. */
export async function updateFahrerKunde(
  id: string, updates: FahrerKundeInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of KUNDE_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) d[camelToSnake(key)] = updates[key];
    }
    const r = await client.from('fahrer_kunden').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/** Löscht einen Fahrer-Kunden. */
export async function deleteFahrerKunde(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_kunden').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// FAHRER EIGENE AUFTRÄGE
// ════════════════════════════════════════════════════════════════════

/** Lädt alle eigenen Aufträge eines Fahrers (nicht Marktplatz). */
export async function getFahrerAuftraege(fahrerId: string): Promise<{
  data: FahrerAuftrag[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_auftraege').select('*').eq('fahrer_id', fahrerId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map((row) => mapFahrerAuftragFromDb(row)).filter((r): r is FahrerAuftrag => r !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Legt einen neuen eigenen Auftrag für den Fahrer an. */
export async function createFahrerAuftrag(
  fahrerId: string, a: FahrerAuftragCreateInput,
): Promise<{ data: FahrerAuftrag | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      // `nr` vergibt der Trigger tr_set_fahrer_auftrag_nr (Migration 129).
      fahrer_id: fahrerId,
      titel: a.titel || '',
      kunde_id: a.kundeId || '', kunde_name: a.kundeName || '',
      abhol: a.abhol || '', ziel: a.ziel || '',
      datum: a.datum || '', zeit: a.zeit || '',
      // Zustellung (Migration 148, 21.10.2026) — vorher gingen beide verloren.
      entlade_datum: a.entladeDatum || null, entlade_zeit: a.entladeZeit || '',
      gewicht: a.gewicht || '', groesse: a.groesse || '',
      preis: a.preis || '', notizen: a.notizen || '',
      status: a.status || 'offen',
      packstuecke: a.packstuecke || [], foto_doku: a.fotoDoku || [],
    };
    const r = await client.from('fahrer_auftraege').insert(d).select().single();
    return { data: r.data ? mapFahrerAuftragFromDb(r.data as Record<string, unknown>) : null, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Aktualisiert einen eigenen Fahrer-Auftrag. */
export async function updateFahrerAuftrag(
  id: string, updates: FahrerAuftragUpdateInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of AUFTRAG_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) {
        // fotoDoku → foto_doku (camelToSnake handles this)
        d[camelToSnake(key)] = updates[key];
      }
    }
    const r = await client.from('fahrer_auftraege').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/** Löscht einen eigenen Fahrer-Auftrag. */
export async function deleteFahrerAuftrag(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_auftraege').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// FAHRER EIGENE RECHNUNGEN
// ════════════════════════════════════════════════════════════════════

/** Lädt alle Rechnungs-Vorlagen eines Fahrers. */
export async function getFahrerRechnungen(fahrerId: string): Promise<{
  data: FahrerRechnung[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_rechnungen').select('*').eq('fahrer_id', fahrerId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map((row) => mapFahrerRechnungFromDb(row)).filter((r): r is FahrerRechnung => r !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Legt eine neue Fahrer-Rechnung an. */
export async function createFahrerRechnung(
  fahrerId: string, re: FahrerRechnungCreateInput,
): Promise<{ data: FahrerRechnung | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      fahrer_id: fahrerId,
      rechnungs_nr: re.rechnungsNr || '',
      kunde_id: re.kundeId || '', kunde_name: re.kundeName || '',
      kunde_adresse: re.kundeAdresse || '', kunde_plz: re.kundePlz || '',
      kunde_ort: re.kundeOrt || '', kunde_email: re.kundeEmail || '',
      kunde_tel: re.kundeTel || '', auftrag_id: re.auftragId || '',
      datum: re.datum || '', leistungsdatum: re.leistungsdatum || '',
      faellig: re.faellig || '', positionen: re.positionen || [],
      notizen: re.notizen || '', zahlungsziel: re.zahlungsziel || '',
      kunden_ref: re.kundenRef || '', kennzeichen: re.kennzeichen || '',
      route: re.route || '', skonto: re.skonto || '',
      kleinunternehmer: !!re.kleinunternehmer,
      sum_netto: re.sumNetto || 0, sum_mwst: re.sumMwst || 0,
      sum_brutto: re.sumBrutto || 0,
      bank_iban: re.bankIban || '', bank_bic: re.bankBic || '',
      bank_name: re.bankName || '', steuernr: re.steuernr || '',
      status: re.status || 'offen',
      mahnstufe: re.mahnstufe || 0, letzte_mahnung: re.letzteMahnung || null,
      locked: !!re.locked,
    };
    const r = await client.from('fahrer_rechnungen').insert(d).select().single();

    // Audit-075 (16.05.2026): Postgres-Constraint-Fehler in strukturierte
    // ApiError mit i18n-Key übersetzen, damit das Frontend dem User eine
    // freundliche Toast-Meldung zeigen kann statt eines Postgres-Texts.
    if (r.error) {
      const pgCode = (r.error as { code?: string }).code;
      const pgMsg = (r.error as { message?: string }).message ?? '';

      // 23505 = unique_violation → unser UNIQUE(fahrer_id, rechnungs_nr)
      if (pgCode === '23505' && pgMsg.includes('fahrer_rechnungen_unique_nr_per_fahrer')) {
        return { data: null, error: { code: 'hcRechnungsNrBereitsVergeben' } };
      }

      // 23514 = check_violation → unser Plausibilitäts-CHECK
      if (pgCode === '23514' && pgMsg.includes('fahrer_rechnungen_betrag_plausibel')) {
        return { data: null, error: { code: 'hcRechnungBetragUnplausibel' } };
      }
    }

    return { data: r.data ? mapFahrerRechnungFromDb(r.data as Record<string, unknown>) : null, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Aktualisiert eine Fahrer-Rechnung.
 *
 * ACHTUNG: Wenn die Rechnung GoBD-gelockt ist (`locked=true`), sollten
 * keine inhaltlichen Änderungen mehr erfolgen — das wird im Frontend
 * geprüft, nicht hier.
 */
export async function updateFahrerRechnung(
  id: string, updates: FahrerRechnungUpdateInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of RECHNUNG_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) {
        // Sonderbehandlung: locked muss boolean sein
        if (key === 'locked') {
          d[camelToSnake(key)] = !!updates[key];
        } else if (key === 'kleinunternehmer') {
          d[camelToSnake(key)] = !!updates[key];
        } else {
          d[camelToSnake(key)] = updates[key];
        }
      }
    }
    const r = await client.from('fahrer_rechnungen').update(d).eq('id', id);

    // Audit-075 (16.05.2026): Postgres-Constraint-Fehler mappen.
    // Beim Update kann der Plausibilitäts-CHECK feuern (z.B. wenn
    // jemand sum_netto manipuliert ohne sum_brutto anzupassen).
    // UNIQUE kann beim Update auch feuern, falls die Nummer geändert
    // wird (auch wenn die Frontend-Whitelist rechnungsNr blockiert —
    // Defense-in-Depth).
    if (r.error) {
      const pgCode = (r.error as { code?: string }).code;
      const pgMsg = (r.error as { message?: string }).message ?? '';

      if (pgCode === '23505' && pgMsg.includes('fahrer_rechnungen_unique_nr_per_fahrer')) {
        return { error: { code: 'hcRechnungsNrBereitsVergeben' } };
      }

      if (pgCode === '23514' && pgMsg.includes('fahrer_rechnungen_betrag_plausibel')) {
        return { error: { code: 'hcRechnungBetragUnplausibel' } };
      }
    }

    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/** Löscht eine Fahrer-Rechnung. */
export async function deleteFahrerRechnung(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_rechnungen').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// FAHRER BUCHUNGEN (Einnahmen/Ausgaben)
// ════════════════════════════════════════════════════════════════════

/** Lädt alle Buchungen eines Fahrers. */
export async function getFahrerBuchungen(fahrerId: string): Promise<{
  data: FahrerBuchung[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_buchungen').select('*').eq('fahrer_id', fahrerId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map((row) => mapFahrerBuchungFromDb(row)).filter((r): r is FahrerBuchung => r !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Legt eine neue Buchung an. */
export async function createFahrerBuchung(
  fahrerId: string, b: FahrerBuchungCreateInput,
): Promise<{ data: FahrerBuchung | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      fahrer_id: fahrerId,
      auftrag_id: b.auftragId || '', datum: b.datum || '',
      typ: b.typ || 'einnahme', kategorie: b.kategorie || '',
      beschreibung: b.beschreibung || '', betrag: b.betrag || 0,
      kunde: b.kunde || '', quelle: b.quelle || '',
    };
    const r = await client.from('fahrer_buchungen').insert(d).select().single();
    return { data: r.data ? mapFahrerBuchungFromDb(r.data as Record<string, unknown>) : null, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Aktualisiert eine Buchung. */
export async function updateFahrerBuchung(
  id: string, updates: FahrerBuchungUpdateInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of BUCHUNG_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) d[camelToSnake(key)] = updates[key];
    }
    const r = await client.from('fahrer_buchungen').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/** Löscht eine einzelne Buchung. */
export async function deleteFahrerBuchung(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_buchungen').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/** Löscht alle Buchungen eines bestimmten Auftrags. */
export async function deleteFahrerBuchungByAuftrag(
  fahrerId: string, auftragId: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_buchungen').delete()
      .eq('fahrer_id', fahrerId).eq('auftrag_id', auftragId);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// TOUREN-PROTOKOLL (ehemals Fahrtenbuch)
// ════════════════════════════════════════════════════════════════════

/** Lädt das komplette Touren-Protokoll eines Fahrers. */
export async function getFahrten(fahrerId: string): Promise<{
  data: Fahrt[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_fahrtenbuch').select('*').eq('fahrer_id', fahrerId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map((row) => mapFahrtFromDb(row)).filter((r): r is Fahrt => r !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Legt einen neuen Eintrag im Touren-Protokoll an. */
export async function createFahrt(
  fahrerId: string, f: FahrtCreateInput,
): Promise<{ data: Fahrt | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      fahrer_id: fahrerId,
      datum: f.datum || '', start_ort: f.startOrt || '',
      ziel_ort: f.zielOrt || '', km_start: f.kmStart || '',
      km_ende: f.kmEnde || '', km_gefahren: f.kmGefahren || 0,
      kunde: f.kunde || '', zweck: f.zweck || '',
      kennzeichen: f.kennzeichen || '', notizen: f.notizen || '',
      quell_id: f.quellId || '',
    };
    const r = await client.from('fahrer_fahrtenbuch').insert(d).select().single();
    return { data: r.data ? mapFahrtFromDb(r.data as Record<string, unknown>) : null, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Aktualisiert einen Touren-Protokoll-Eintrag. */
export async function updateFahrt(
  id: string, updates: FahrtUpdateInput,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of FAHRT_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) d[camelToSnake(key)] = updates[key];
    }
    const r = await client.from('fahrer_fahrtenbuch').update(d).eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

/** Löscht einen Touren-Protokoll-Eintrag. */
export async function deleteFahrt(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_fahrtenbuch').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// FAHRER-VORLAGEN
// ════════════════════════════════════════════════════════════════════

/** Lädt alle wiederverwendbaren Auftragsvorlagen eines Fahrers. */
export async function getFahrerVorlagen(fahrerId: string): Promise<{
  data: Array<Record<string, unknown>>;
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_vorlagen').select('*').eq('fahrer_id', fahrerId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map((row) => mapFahrerVorlageFromDb(row)).filter((r): r is Record<string, unknown> => r !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Legt eine neue Auftragsvorlage an. */
export async function createFahrerVorlage(
  fahrerId: string, v: FahrerVorlageInput,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      fahrer_id: fahrerId,
      kunde_name: v.kundeName || '', kunde_id: v.kundeId || '',
      abhol: v.abhol || '', ziel: v.ziel || '',
      preis: v.preis || '', gewicht: v.gewicht || '',
      groesse: v.groesse || '', notizen: v.notizen || '',
    };
    const r = await client.from('fahrer_vorlagen').insert(d).select().single();
    return { data: r.data ? mapFahrerVorlageFromDb(r.data as Record<string, unknown>) : null, error: r.error };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Löscht eine Auftragsvorlage. */
export async function deleteFahrerVorlage(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_vorlagen').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// fahrer_angebot_dismissed (Migration 062)
// ════════════════════════════════════════════════════════════════════
// Pro Fahrer: weggeblendete obsolete Angebote auf stornierte/abgelaufene
// Aufträge. Das Angebot bleibt in der DB (auftraege.angebote-Spalte) —
// nur die UI-Anzeige in "Meine Angebote → Nicht mehr verfügbar" wird
// für diesen Fahrer ausgeblendet.

/** Lädt alle vom Fahrer weggeblendeten Auftrags-IDs. */
export async function getFahrerAngebotDismissed(
  fahrerId: string,
): Promise<{ data: string[]; error: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_angebot_dismissed')
      .select('auftrag_id')
      .eq('fahrer_id', fahrerId);
    if (r.error) return { data: [], error: r.error };
    const ids = (r.data || [])
      .map((row) => (row as { auftrag_id?: unknown }).auftrag_id)
      .filter((id): id is string => typeof id === 'string');
    return { data: ids, error: null };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/** Markiert einen Auftrag als "weggeblendet" für diesen Fahrer. Idempotent
 *  durch Composite-PK — doppelter Insert wird von Postgres mit unique_violation
 *  abgewiesen, was wir hier still ignorieren (gewünschtes Verhalten). */
export async function dismissFahrerAngebot(
  fahrerId: string,
  auftragId: string,
): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_angebot_dismissed')
      .insert({ fahrer_id: fahrerId, auftrag_id: auftragId });
    // unique_violation (23505) = bereits weggeblendet → Erfolg
    if (
      r.error &&
      typeof r.error === 'object' &&
      'code' in r.error &&
      (r.error as { code?: string }).code === '23505'
    ) {
      return { error: null };
    }
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// GEMERKTE STRECKEN („Freie Rückfahrten", Migration 123/124)
// ════════════════════════════════════════════════════════════════════
//
// RLS sorgt dafür, dass jeder nur seine eigenen Strecken sieht und ändert;
// anlegen dürfen Fahrer und Speditionen (Firma mit Bieten-Recht, dann mit
// Pflicht-Fahrzeug — Migration 125). `fahrer_id` setzt die Datenbank selbst
// (DEFAULT auth.uid()) — der Client schickt sie bewusst nicht mit. Der Name
// heißt trotz Speditionen weiter `fahrer_id`: Wem gehört die Strecke.

/** Punkt auf der Karte, wie er in `von_geo` / `nach_geo` steht. */
export interface StreckenGeo {
  lat: number;
  lng: number;
}

/** Eine gemerkte Strecke. */
export interface GemerkteStrecke {
  id: string;
  vonOrt: string;
  vonGeo: StreckenGeo;
  nachOrt: string;
  nachGeo: StreckenGeo;
  /** YYYY-MM-DD */
  freiAb: string;
  /** YYYY-MM-DD */
  freiBis: string;
  radiusKm: number;
  /** Fahrzeug der Strecke; null = Fahrzeug aus dem Profil (Fahrer). */
  fahrzeugTyp: string | null;
  /** Ausstattung der Strecke; null = Ausstattung aus dem Profil (Fahrer). */
  ausstattung: Record<string, true> | null;
}

/**
 * Eingabe zum Anlegen. Ein Fahrer lässt Fahrzeug und Ausstattung leer →
 * die Datenbank nimmt das Profil. Eine Spedition gibt beides mit (Fahrzeug
 * ist bei ihr Pflicht, Migration 125). `auftragId` ist gesetzt, wenn die
 * Strecke aus dem Vorschlag „Rückfahrt merken?" einer Auftragskarte stammt
 * (Paket 4).
 */
export type GemerkteStreckeInput = Omit<GemerkteStrecke, 'id' | 'fahrzeugTyp' | 'ausstattung'> & {
  auftragId?: string | null;
  fahrzeugTyp?: string | null;
  ausstattung?: Record<string, true> | null;
};

/** Spalten, die beim Laden und nach dem Anlegen gelesen werden. */
const STRECKE_SPALTEN =
  'id, von_ort, von_geo, nach_ort, nach_geo, frei_ab, frei_bis, radius_km, fahrzeug_typ, ausstattung';

/**
 * Liest eine Zeile aus `fahrer_rueckfahrten` defensiv ein.
 *
 * @returns null, wenn Pflichtfelder fehlen (dann wird die Zeile übersprungen)
 */
function mapStreckeFromDb(row: Record<string, unknown>): GemerkteStrecke | null {
  const geo = (v: unknown): StreckenGeo | null => {
    const g = v as { lat?: unknown; lng?: unknown } | null;
    if (!g || typeof g.lat !== 'number' || typeof g.lng !== 'number') return null;
    return { lat: g.lat, lng: g.lng };
  };
  const vonGeo = geo(row.von_geo);
  const nachGeo = geo(row.nach_geo);
  if (typeof row.id !== 'string' || !vonGeo || !nachGeo) return null;
  return {
    id: row.id,
    vonOrt: typeof row.von_ort === 'string' ? row.von_ort : '',
    vonGeo,
    nachOrt: typeof row.nach_ort === 'string' ? row.nach_ort : '',
    nachGeo,
    freiAb: typeof row.frei_ab === 'string' ? row.frei_ab : '',
    freiBis: typeof row.frei_bis === 'string' ? row.frei_bis : '',
    radiusKm: typeof row.radius_km === 'number' ? row.radius_km : 100,
    fahrzeugTyp: typeof row.fahrzeug_typ === 'string' && row.fahrzeug_typ.trim() ? row.fahrzeug_typ : null,
    ausstattung: mapAusstattung(row.ausstattung),
  };
}

/**
 * Ausstattung aus der Datenbank: nur Schlüssel mit Wert true.
 *
 * @returns null, wenn die Spalte leer oder kein Objekt ist (= aus dem Profil)
 */
function mapAusstattung(v: unknown): Record<string, true> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out: Record<string, true> = {};
  for (const [k, w] of Object.entries(v as Record<string, unknown>)) {
    if (w === true) out[k] = true;
  }
  return out;
}

/**
 * Lädt die eigenen aktiven Strecken, sortiert nach „frei ab".
 *
 * Vorher werden abgelaufene Strecken (letzter freier Tag vor `heute`)
 * gelöscht — Datensparsamkeit: Wo jemand einmal unterwegs war, wird nicht
 * länger gespeichert als gebraucht. Scheitert das Löschen, wird trotzdem
 * geladen; das Matching ignoriert abgelaufene Strecken ohnehin.
 *
 * @param heute Heutiges Datum YYYY-MM-DD (Ortszeit des Geräts)
 */
export async function getGemerkteStrecken(
  heute: string,
): Promise<{ data: GemerkteStrecke[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const del = await client.from('fahrer_rueckfahrten').delete().lt('frei_bis', heute);
    if (del.error) console.warn('getGemerkteStrecken (aufräumen):', del.error.message);
    const r = await client
      .from('fahrer_rueckfahrten')
      .select(STRECKE_SPALTEN)
      .eq('status', 'aktiv')
      .order('frei_ab', { ascending: true });
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map(mapStreckeFromDb).filter((s): s is GemerkteStrecke => s !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Legt eine Strecke an. Grenzen (Anzahl, Zeitraum) prüft der Aufrufer
 * vorher mit `rueckfahrt-strecke.ts`; die Datenbank prüft zusätzlich
 * Radius, Zeitraum-Reihenfolge und Koordinaten.
 *
 * Datensparsamkeit (11.10.2026): Gespeichert werden nur der Ortsname
 * (`extractCity`, vorher oft volle Adresse bzw. „Hausnr., Straße, Ort"
 * vom GPS-Knopf) und eine auf ≈ 1 km gerundete Position (`grobePosition`).
 * Der Text dient nur der Anzeige, der Abgleich braucht keine Hausnummer.
 */
export async function createGemerkteStrecke(
  s: GemerkteStreckeInput,
): Promise<{ data: GemerkteStrecke | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('fahrer_rueckfahrten')
      .insert({
        von_ort: extractCity(s.vonOrt).slice(0, 300),
        von_geo: grobePosition(s.vonGeo),
        nach_ort: extractCity(s.nachOrt).slice(0, 300),
        nach_geo: grobePosition(s.nachGeo),
        frei_ab: s.freiAb,
        frei_bis: s.freiBis,
        radius_km: s.radiusKm,
        auftrag_id: s.auftragId || null,
        fahrzeug_typ: s.fahrzeugTyp || null,
        ausstattung: s.ausstattung ?? null,
      })
      .select(STRECKE_SPALTEN)
      .single();
    return {
      data: r.data ? mapStreckeFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Löscht eine eigene Strecke. */
export async function deleteGemerkteStrecke(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('fahrer_rueckfahrten').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}
